<template>
    <b-modal
        v-if="entity"
        v-model="modal.state"
        :header-class="{
            'border-bottom pr-3 pl-0 py-2': isMobile,
            'property-saved-searches-edit-modal-heading px-4': !isMobile,
        }"
        body-class="p-0"
        footer-class="p-3 p-sm-4"
        size="sm"
        scrollable
        no-close-on-backdrop
        no-close-on-esc
        title="Edit Saved Search"
        dialog-class="property-saved-searches-edit-modal"
        @hidden="handleHidden"
    >
        <template
            v-if="!isMobile"
            v-slot:modal-header="{ close }"
        >
            <div class="d-flex justify-content-between align-items-center">
                <div class="h2 m-0">
                    Edit Saved Search
                </div>

                <b-button
                    class="property-saved-searches-edit-modal-title-close-button position-absolute mr-2"
                    variant="link"
                    @click="close()"
                >
                    <i class="fal fa-times" />
                </b-button>
            </div>
        </template>
        <template
            v-else
            v-slot:modal-header-close
        >
            <i class="far fa-arrow-left text-gray-700" />
        </template>
        <b-form @submit.prevent="handleSubmit">
            <b-row no-gutters>
                <b-col>
                    <template v-if="isMobile">
                        <b-form-group
                            v-if="currentSearchAlertSubscriber"
                            class="py-3 px-4 border-bottom mb-0"
                        >
                            <b-form-checkbox
                                name="checkbox-subscribe"
                                class="subscribe-checkbox"
                                v-model="currentSearchAlertSubscriber.enabled"
                            >
                                Notify me when new homes match my criteria
                            </b-form-checkbox>
                        </b-form-group>

                        <div
                            v-if="users.length"
                            class="d-flex align-items-center font-size-sm text-gray-800 px-4 py-3 justify-content-between"
                            @click.stop="displayShareModal"
                        >
                            <span class="d-flex align-items-center share-button">
                                Shared with
                                <storage-avatar-nested
                                    class="ml-2"
                                    :users="users"
                                />
                            </span>
                            <span class="share-caret">
                                <i class="far fa-angle-right" />
                            </span>
                        </div>

                        <b-button
                            v-else
                            block
                            squared
                            class="d-flex align-items-center justify-content-between px-4 py-3 border-0 share-button"
                            @click.stop="displayShareModal"
                        >
                            Share with Member
                            <span>
                                <i class="far fa-angle-right" />
                            </span>
                        </b-button>
                    </template>
                    <b-form-group
                        label="NAME"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <div class="mx-4 mt-4 mb-2">
                            <b-form-input
                                class="form-input"
                                placeholder="Name your search"
                                v-model="entity.name"
                            />
                        </div>
                    </b-form-group>
                    <b-form-group
                        label="LOCATION"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <div class="px-4">
                            <property-search-input
                                class="modal-search-input"
                                v-model="entity.data.search"
                            />
                        </div>
                    </b-form-group>
                    <b-form-group
                        label="PRICE"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-row class="mx-4 mt-4 mb-2">
                            <range-dropdown
                                class="p-0"
                                v-model="entity.data.price"
                                :options="FILTER_OPTIONS.PRICES"
                            />
                        </b-row>
                    </b-form-group>
                    <b-form-group
                        label="BEDROOMS"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-button-group class="px-4 pt-4 pb-2 w-100">
                            <b-button
                                v-for="(option, index) in FILTER_OPTIONS.BEDS"
                                :key="option + index"
                                size="lg"
                                class="px-0"
                                :class="{
                                            'btn-selected':
                                            String(get(entity,'data.beds.value')) === String(option.value)
                                            }"
                                @click="updateBedOptions(option)"
                            >
                                {{ option.label }}
                            </b-button>
                        </b-button-group>
                    </b-form-group>
                    <b-form-group
                        label="BATHROOMS"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-button-group class="px-4 pt-4 pb-2 w-100">
                            <b-button
                                v-for="(option, index) in FILTER_OPTIONS.BATHS"
                                :key="option + index"
                                size="lg"
                                class="px-0"
                                :class="{
                                            'btn-selected':
                                            String(get(entity,'data.baths.value')) === String(option.value)
                                            }"
                                @click="updateBathOptions(option)"
                            >
                                {{ option.label }}
                            </b-button>
                        </b-button-group>
                    </b-form-group>
                    <b-form-group
                        label="SQUARE FEET"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-row class="mx-4 mt-4 mb-2">
                            <range-dropdown
                                class="p-0"
                                v-model="entity.data.squareFeet"
                                :options="FILTER_OPTIONS.SQUARE_FEET"
                            />
                        </b-row>
                    </b-form-group>
                    <b-form-group
                        label="HOME TYPE"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <div
                            class="d-flex justify-content-between form-checkbox my-2 px-4"
                            v-for="homeType in FILTER_OPTIONS.HOME_TYPES"
                            :key="homeType.value"
                        >
                            <div>
                                {{ homeType.label }}
                            </div>
                            <b-form-checkbox
                                name="checkbox-hometype"
                                :value="homeType.value"
                                v-model="entity.data.homeTypes"
                            />
                        </div>
                    </b-form-group>
                    <b-form-group
                        label="LISTING TYPE & STATUS"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <div
                            class="d-flex justify-content-between form-checkbox my-2 px-4"
                            v-for="(saleType, index) in FILTER_OPTIONS.SALE_TYPES"
                            :key="index + saleType.label"
                        >
                            <div>
                                {{ saleType.label }}
                            </div>
                            <b-form-checkbox
                                name="checkbox-hometype"
                                :value="saleType"
                                v-model="entity.data.sale"
                            />
                        </div>
                    </b-form-group>
                    <b-form-group
                        label="YEAR BUILT"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-row class="mx-4 mt-4 mb-2">
                            <range-dropdown
                                class="p-0"
                                v-model="entity.data.yearBuilt"
                                :options="FILTER_OPTIONS.YEAR_BUILT"
                            />
                        </b-row>
                    </b-form-group>
                    <b-form-group
                        label="KEYWORDS"
                        class="mb-0"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-row class="ml-4 mr-4 mt-4 mb-2">
                            <keyword-type-ahead
                                class="w-100"
                                v-model="entity.data.keywords"
                                :options="FILTER_OPTIONS.KEYWORDS"
                            />
                        </b-row>
                    </b-form-group>
                    <b-form-group
                        label="LOT SIZE"
                        label-class="sub-heading-container h3 font-weight-bold p-2 pl-4"
                    >
                        <b-row class="mx-4 mt-4 mb-2">
                            <range-dropdown
                                class="p-0"
                                v-model="entity.data.lotSize"
                                :options="FILTER_OPTIONS.LOT_SIZE"
                            />
                        </b-row>
                    </b-form-group>
                </b-col>
            </b-row>
        </b-form>
        <template v-slot:modal-footer>
            <b-button
                block
                type="submit"
                class="save-button p-3"
                :disabled="isLoading"
                @click.stop="handleSubmit"
            >
                <b-spinner
                    v-if="isLoading"
                    class="mb-2"
                    small
                />
                Save Search
            </b-button>
        </template>
    </b-modal>
</template>

<script>
    import './PropertySavedSearchesEditModal.scss';
    import PropertySearchFilterAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/PropertySearchFilterAppResource";
    import ShowNotificationMixin from '@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin';
    import FormErrorsMixin from "@milestoneslabs/milestones-ui-kit/src/components/FormErrors/FormErrorsMixin";
    import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
    import SavedSearchAlertsMixin from "../../../../mixins/SavedSearchAlertsMixin";
    import * as FILTER_OPTIONS from '../../../../../helpers/filterOptions';
    import {AppResourceCollection} from "@milestoneslabs/milestones-js-engine";
    import {PROPERTY_SAVED_SEARCHES_SHARE_MODAL_DISPLAY}
        from "../../PropertySavedSearchesShareModal/PropertySavedSearchesShareModal";
    import SearchAlertSubscriberAppResource, {
        NOTIFICATION_FREQUENCY,
        NOTIFICATION_TYPE
    } from "@milestoneslabs/milestones-core-js/src/AppResources/SearchAlertSubscriberAppResource";
    import get from 'lodash/get';

    import RangeDropdown
        from "../../../GroupPropertySearchHeader/GroupPropertySearchFilters/GroupDropdowns/RangeDropdown/RangeDropdown";
    import StorageAvatarNested from "../../../StorageImage/StorageAvatarNested";
    import PropertySearchInput
        from '../../../GroupPropertySearchHeader/PropertySearchHeader/PropertySearchInput/PropertySearchInput';
    // eslint-disable-next-line max-len
    import KeywordTypeAhead from '../../../GroupPropertySearchHeader/GroupPropertySearchFilters/PropertySearchFilters/KeywordTypeAhead/KeywordTypeAhead';

    export const PROPERTY_SAVED_SEARCHES_EDIT_MODAL_DISPLAY = 'PropertySavedSearchesEditModal:display';
    export const PROPERTY_SAVED_SEARCHES_EDIT_MODAL_SAVED = 'PropertySavedSearchesEditModal:saved';

    export default {
        name: "PropertySavedSearchesEditModal",

        mixins: [
            SavedSearchAlertsMixin,
            ShowNotificationMixin,
            FormErrorsMixin,
            IsMobileMixin,
        ],

        components: {
            RangeDropdown,
            PropertySearchInput,
            KeywordTypeAhead,
            StorageAvatarNested,
        },

        data() {
            return {
                FILTER_OPTIONS,
                filterKeys: [
                    'price',
                    'beds',
                    'baths',
                    'squareFeet',
                    'homeTypes',
                    'sale',
                    'keywords',
                    'lotSize',
                ],
                entity: {
                    name: '',
                    location: '',
                    data: {},
                },
                modal: {
                    state: false,
                },
                subProjectMembers: [],
                currentSearchAlertSubscriber: null,
                event: Math.random().toString(36).substr(2, 9),
            };
        },

        computed: {
            searchAlertSubscribers() {
                if (this.entity && this.entity.searchAlertSubscribers) {
                    return this.entity.searchAlertSubscribers.toArray();
                }
                return [];
            },

            users() {
                const users = [];
                for (let projectMember of this.subProjectMembers) {
                    users.push(projectMember.user);
                }
                return users;
            },
        },

        async beforeMount() {
            this.$bus.$on(PROPERTY_SAVED_SEARCHES_EDIT_MODAL_DISPLAY, ({entity}) => {
                if (!(entity instanceof PropertySearchFilterAppResource)) {
                    throw new Error('Invalid payload, entity should be an instance of PropertySearchFilterAppResource');
                }

                this.init(entity);
            });
            this.$bus.$on(this.event, this.handleShareEvent);
        },

        beforeDestroy() {
            this.$bus.$off(this.event, this.handleShareEvent);
        },

        methods: {
            get,

            handleSubmit() {
                this.entity._dirty['data'] = true;

                let projectMembers = [...this.subProjectMembers];
                if (this.currentSearchAlertSubscriber.enabled) {
                    projectMembers.push(this.currentProjectMember);
                }
                this.updateSubscribers(this.entity, projectMembers);

                this.entity.once('saved', () => {
                    this.entity.once('fetched', () => {
                        this.modal.state = false;
                        this.$bus.$emit(PROPERTY_SAVED_SEARCHES_EDIT_MODAL_SAVED, {entity: this.entity});
                        this.showNotification({
                            message: 'Saved Search Updated!',
                            type: 'success',
                            toaster: 'layoutToaster',
                        });
                    });

                });
            },

            async init(entity) {
                let tempEntity = new PropertySearchFilterAppResource({uuid: entity.uuid});
                try {
                    await tempEntity.fetch([
                        'searchAlertSubscribers.projectMember.user',
                        'searchAlertSubscribers.projectMember.workspaceMembershipType',
                    ]);
                } catch (e) {
                    this.showNotification({
                        message: 'Can not load saved search data. Please try again later.',
                        type: 'error',
                        toaster: 'layoutToaster',
                    });
                }
                this.entity = tempEntity;
                this.subProjectMembers = [];
                this.entity.searchAlertSubscribers.forEach((sub) => {
                    if (sub.enabled && (sub.projectMember.uuid !== this.currentProjectMemberUuid)) {
                        this.subProjectMembers.push(sub.projectMember);
                    }
                });
                this.currentSearchAlertSubscriber = this.getCurrentSearchAlertSubscriber();
                if (!this.hasSearchFilter(this.entity.data.search)) {
                    this.entity.data.search = {
                        label: '',
                        value: null,
                        field: null,
                    };
                }
                this.modal.state = true;
            },

            getCurrentSearchAlertSubscriber() {
                let existingSubscriber = this.searchAlertSubscribers.find(
                    sub => sub.projectMemberUuid === this.currentProjectMemberUuid
                );

                if (existingSubscriber) {
                    return existingSubscriber;
                }

                return new SearchAlertSubscriberAppResource({
                    attributes: {
                        propertySearchFilterUuid: this.entity.uuid,
                        projectMemberUuid: this.currentProjectMemberUuid,
                        notificationType: NOTIFICATION_TYPE.EMAIL,
                        notificationFrequencyInMins: NOTIFICATION_FREQUENCY.FOUR_HOURS,
                        enabled: false,
                    }
                });
            },

            hasSearchFilter(filterData) {
                return !(!filterData || !filterData.field || !filterData.label || !filterData.value);
            },

            updateBedOptions(value) {
                this.entity.data.beds = value;
            },

            updateBathOptions(value) {
                this.entity.data.baths = value;
            },

            displayShareModal() {
                let collection = new AppResourceCollection('workspace-organization-Member');
                collection.addResource(this.subProjectMembers);
                this.$bus.$emit(
                    PROPERTY_SAVED_SEARCHES_SHARE_MODAL_DISPLAY,
                    {
                        entity: collection,
                        event: this.event,
                    }
                );
            },

            handleShareEvent(projectMembers) {
                if (!Array.isArray(projectMembers)) {
                    throw new Error('Invalid payload, projectMembers should be an array.');
                }
                this.subProjectMembers = projectMembers;
            },

            handleHidden() {
                this.subProjectMembers = [];
                this.currentSearchAlertSubscriber = null;
                this.entity = {
                    name: '',
                    location: '',
                    data: {},
                };
            },
        },
    };
</script>
